import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

import { NotifyService } from "../../../notify/notify.service";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { TOAST } from "app/notify/toast/toast-type.enum";
import { BackendErrorPipe } from "app/shared/backend/backend-error.pipe";
import { User } from "app/user/user.model";
import { BaseStandardQuote } from "app/quote/models/quote-base-standard.model";
import { QuoteRepository } from "app/quote/quote.repository";
import { Subscription } from "rxjs";
import { Base64 } from "js-base64";
import { Quote } from "app/quote/quote.type";
import { ButtonDirective } from "primeng/button";
import { Popover } from "primeng/popover";
import { InputText } from "primeng/inputtext";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";

@Component({
  selector: "app-quote-ai",
  templateUrl: "./quote-ai.component.html",
  styleUrls: ["./quote-ai.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InputText,
    ButtonDirective,
    Popover,
    PopoverArrowDirective,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class QuoteAiComponent implements OnInit, OnDestroy {
  protected quoteRepository = inject(QuoteRepository);
  protected formBuilder = inject(UntypedFormBuilder);
  protected notifyService = inject(NotifyService);

  public readonly quote = input<BaseStandardQuote>(undefined);
  public readonly disabled = input(false);
  readonly closeView = output();
  readonly refreshQuote = output<Quote>();
  public user: User = null;
  public loading = false;
  public form: UntypedFormGroup;
  private readonly panel = viewChild<Popover>("panel");
  private subscription = new Subscription();

  public ngOnInit() {
    this.form = this.createForm();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      text: [null],
    });
  }

  public extract() {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository
        .ai(this.quote().getId(), Base64.encode(this.form.value.text))
        .subscribe({
          next: (response): void => {
            this.loading = false;
            if (response.confidence !== null && response.confidence < 80) {
              this.notifyService.toast(
                TOAST.WARN,
                "Low confidence",
                "AI confidence is low — please verify all extracted values.",
                true,
              );
            }
            this.fetchQuote();
          },
          error: (err): void => {
            this.notifyService.toast(
              TOAST.ERROR,
              "Can't add this item",
              new BackendErrorPipe().transform(err, "string"),
            );
            this.loading = false;
          },
        }),
    );
  }

  private fetchQuote(): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository.getQuote(this.quote().getId()).subscribe({
        next: (response): void => {
          this.loading = false;
          this.refreshQuote.emit(response);
          this.close();
        },
        error: (err): void => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Can't get quote",
            new BackendErrorPipe().transform(err, "string"),
          );
          this.loading = false;
        },
      }),
    );
  }

  public onShow() {
    this.form.patchValue({ text: this.quote()?.getInput() ?? null });
  }

  public close() {
    this.panel()?.hide();
  }
}
